// 通報のダイアログ。理由が無いと運営が判断できないので、
// お問い合わせと同じく「絵で理由を選ぶ」＋「自由に書く」の形にする。
import { useEffect, useState } from "react";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { InScreenModal } from "./ScreenModal";
import { BuilderModalPanel, BuilderModalAccent } from "./BuilderModalPanel";
import { PillSize, PillColor, BuilderPillButton } from "./BuilderPillButton";

/** api の CommentReportReason と同じ値 */
export enum ReportReason {
  SPAM = "spam",
  OTHER = "other",
  COPYRIGHT = "copyright",
  INAPPROPRIATE = "inappropriate",
  DISLIKE_EXPRESSION = "dislike_expression",
}

// 絵は正方形なので、切らずに収めて余白を絵の背景色で埋める（bg は画像から実測した色）
const REASONS: {
  reason: ReportReason;
  image: string;
  icon: string;
  bg: string;
  labelKey: string;
}[] = [
  {
    reason: ReportReason.INAPPROPRIATE,
    image: "report_inappropriate",
    bg: "bg-[#f5c6cd]",
    icon: "report",
    labelKey: "MSG_BUILDER_REPORT_REASON_INAPPROPRIATE",
  },
  {
    reason: ReportReason.DISLIKE_EXPRESSION,
    image: "report_dislike",
    bg: "bg-[#bde2ee]",
    icon: "sentiment_dissatisfied",
    labelKey: "MSG_BUILDER_REPORT_REASON_DISLIKE",
  },
  {
    reason: ReportReason.SPAM,
    image: "report_spam",
    bg: "bg-[#faed6f]",
    icon: "content_copy",
    labelKey: "MSG_BUILDER_REPORT_REASON_SPAM",
  },
  {
    reason: ReportReason.COPYRIGHT,
    image: "report_copy",
    bg: "bg-[#cde8c8]",
    icon: "file_copy",
    labelKey: "MSG_BUILDER_REPORT_REASON_COPYRIGHT",
  },
  {
    reason: ReportReason.OTHER,
    image: "report_other",
    bg: "bg-[#dbccee]",
    icon: "more_horiz",
    labelKey: "MSG_BUILDER_REPORT_REASON_OTHER",
  },
];

const ReasonCard = ({
  selected,
  image,
  icon,
  bg,
  label,
  onSelect,
}: {
  selected: boolean;
  image: string;
  /** 絵が配信されていないときに出す代わりの記号 */
  icon: string;
  /** 絵の背景色。contain の余白をこの色で埋めて切れて見えないようにする */
  bg: string;
  label: string;
  onSelect: () => void;
}) => {
  const [noImage, setNoImage] = useState(false);

  return (
    <button
      type="button"
      onClick={onSelect}
      className="type-inherit-font flex-col-el flex-center relative w-[172px] p-0 border-none bg-white rounded-xl overflow-hidden cursor-pointer"
    >
      {noImage ? (
        <div className="flex-col-center w-full h-[112px] bg-gray2-20">
          <span className="material-symbols-outlined type-display text-gray2-100">{icon}</span>
        </div>
      ) : (
        <img
          alt=""
          src={`${Constants.assetHost}/assets/images/${image}.png`}
          onError={() => setNoImage(true)}
          className={`w-full h-[112px] object-contain ${bg}`}
        />
      )}
      <div
        className={`flex-col-center w-full h-[44px] px-1 transition-colors ${
          selected ? "bg-orange-100" : "bg-blue-80"
        }`}
      >
        <p className="type-text type-sm text-white !whitespace-pre">{label}</p>
      </div>
      <div
        className={`flex-col-el absolute w-full h-full rounded-xl box-border ${
          selected ? "border-solid border-4 border-orange-100" : "border-none"
        }`}
      />
    </button>
  );
};

export const BuilderReportDialog = ({
  visible,
  title,
  target,
  message,
  close,
  handleConfirm,
}: {
  visible: boolean;
  title: string;
  /** 通報する相手（ピース名・コメント本文）。何を通報するのか分かるように引用で出す */
  target: string;
  message: string;
  close: () => void;
  handleConfirm: (reason: string, detail: string) => void;
}) => {
  const play = usePlaySound();
  const playConfirm = usePlaySound(`${Constants.assetHost}/assets/sounds/pop-up-off.mp3`);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [detail, setDetail] = useState("");

  // 開くたびに選び直す。前の通報の内容が残っていると誤送信になる
  useEffect(() => {
    if (visible) {
      setReason(null);
      setDetail("");
    }
  }, [visible]);

  // 長いコメントをそのまま出すとダイアログが伸びて枠に収まらない
  const quoted = target.length > 40 ? `${target.slice(0, 40)}…` : target;

  const handleSelect = (next: ReportReason) => () => {
    play();
    setReason(next);
  };

  const handleSend = () => {
    if (!reason) return;
    playConfirm();
    handleConfirm(reason, detail.trim());
  };

  return (
    <InScreenModal open={visible} onBackdropClose={close}>
      <BuilderModalPanel
        accent={BuilderModalAccent.ORANGE}
        icon="flag"
        title={title}
        width={940}
        onClose={close}
      >
        <div className="flex-col-el items-start w-full gap-2">
          <div className="flex-col-el items-start w-full">
            {/* 1行目に対象をくっつける。筐体画面は 572px しかなく、引用だけで1行使えない */}
            {message.split("\n").map((line, index) => (
              <p key={index} className="type-text-left type-body text-textcolor-black">
                {index === 0 ? `「${quoted}」${line}` : line}
              </p>
            ))}
          </div>

          <p className="type-text-left type-body text-textcolor-black w-full">
            {I18n.t("MSG_BUILDER_REPORT_REASON_TITLE")}
          </p>

          <div className="flex-row-el flex-wrap justify-center items-stretch w-full gap-2">
            {REASONS.map((item) => (
              <ReasonCard
                key={item.reason}
                image={item.image}
                icon={item.icon}
                bg={item.bg}
                label={I18n.t(item.labelKey)}
                selected={reason === item.reason}
                onSelect={handleSelect(item.reason)}
              />
            ))}
          </div>

          <div className="flex-col-el items-start w-full">
            <textarea
              rows={1}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder={I18n.t("MSG_BUILDER_REPORT_DETAIL_LABEL")}
              className="type-text-left type-body text-textcolor-black w-full box-border rounded-xl p-3 border-solid border-2 border-gray2-30 outline-none resize-none bg-white focus:border-orange-100 focus:ring-4 focus:ring-orange-100/30"
            />
          </div>
        </div>

        <div className="flex-row-el flex-center w-full gap-4 px-4 pb-2">
          <BuilderPillButton
            color={PillColor.BLUE}
            size={PillSize.MD}
            icon="close"
            label={I18n.t("MSG_STOP_BTN")}
            onClick={close}
          />
          <BuilderPillButton
            color={PillColor.ORANGE}
            size={PillSize.MD}
            icon="flag"
            label={I18n.t("MSG_CONFIRM_BTN")}
            disabled={!reason}
            onClick={handleSend}
          />
        </div>
      </BuilderModalPanel>
    </InScreenModal>
  );
};
